import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

// Define the styled span for the button text *before* sharedButtonStyles if needed,
// or just ensure it's defined before use in the component.
const ButtonTextSpan = styled.span`
    position: relative; /* Establishes stacking context for z-index */
    z-index: 1;       /* Ensures text is above the ::before pseudo-element */
    display: inline-block; /* Ensures proper layout/wrapping within the button */
`;

// Shared styles applied to all button variants
const sharedButtonStyles = css`
    /* Basic styling */
    display: inline-block;
    padding: 12px 30px;
    margin: 10px;
    text-align: center;

    /* Typography */
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    text-decoration: none;

    /* Colors & Appearance */
    background-color: transparent; /* Keep button background transparent */
    color: var(--text-color);
    border: 1px solid var(--primary-color);
    border-radius: 0;        /* Explicitly remove default button rounding */
    appearance: none;        /* Attempt to remove OS-level button styling */
    -webkit-appearance: none;
    -moz-appearance: none;

    /* Special effects */
    position: relative;      /* Needed for ::before positioning and stacking context */
    overflow: hidden;
    cursor: pointer;
    transition: color 0.3s ease; /* Transition only color for performance, transform handles bg */

    /* Hover background effect - animation */
    &:before {
        content: "";
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background-color: var(--primary-color); /* Solid background color */
        transition: transform 0.3s ease;
        /* z-index: -1; <-- REMOVED: We now put text on top with z-index */
    }

    /* Hover state */
    &:hover {
        color: var(--background-color); /* Change text color */
        /* Background is handled by the ::before element */
        border: 1px solid var(--primary-color); /* Explicitly keep border */

        &:before {
            transform: translateX(100%); /* Animate the background in */
        }
    }

    /* Disabled state */
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;

        /* Prevent hover effects on disabled state */
        &:hover {
            color: var(--text-color); /* Keep original text color */
            &:before {
                transform: translateX(0); /* Prevent background animation */
            }
        }
    }

    /* Optional: Consistent Focus State */
    &:focus {
        outline: 2px solid var(--secondary-color); /* Example focus style */
        outline-offset: 2px;
    }
    /* Remove default browser focus styles if you handle it yourself */
    &:focus:not(:focus-visible) {
        outline: none;
    }

    @media (max-width: 480px) {
        padding: 10px 20px; /* Smaller padding */
        font-size: 12px;    /* Smaller font */
    }

    @media (max-width: 320px) {
        padding: 8px 16px;  /* Even smaller for tiny screens */
        margin: 8px;        /* Reduced margin */
    }
`;

// Button as a regular button element
const ButtonElement = styled.button`
    ${sharedButtonStyles}
`;

// Button as a React Router Link
const LinkElement = styled(Link)`
    ${sharedButtonStyles}
`;

// Button as an external anchor
const AnchorElement = styled.a`
    ${sharedButtonStyles}
`;

/**
 * StandardButton - A unified button component for consistent styling
 * Can be rendered as a button, Link (internal), or anchor (external)
 */
const StyledButton = ({
                          to,               // For internal routing (React Router)
                          href,             // For external links
                          download,         // For file downloads (true or filename string)
                          onClick,          // Click handler for button
                          disabled = false, // Disabled state
                          type = 'button',  // Button type (submit, button, reset)
                          children,         // Button text/content
                          style,            // Additional inline styles (Use with caution!)
                          className,        // Additional CSS classes
                          ...rest           // Any other props
                      }) => {
    // Internal link using React Router
    if (to) {
        return (
            <LinkElement
                to={to}
                style={style}
                className={className}
                {...rest}
            >
                {/* Wrap children in the span with z-index */}
                <ButtonTextSpan>{children}</ButtonTextSpan>
            </LinkElement>
        );
    }

    // File download link
    if (href && download !== undefined) {
        const ariaLabel = rest['aria-label'] ||
            (typeof children === 'string' ? `Download ${children}` : 'Download file');

        return (
            <AnchorElement
                href={href}
                download={download === true ? '' : download}
                style={style}
                className={className}
                aria-label={ariaLabel}
                {...rest}
            >
                <ButtonTextSpan>{children}</ButtonTextSpan>
            </AnchorElement>
        );
    }

    // External link (opens in new tab)
    if (href) {
        // Create accessible label for external links
        const ariaLabel = rest['aria-label'] ||
            (typeof children === 'string' ? `${children} (opens in new tab)` : 'Link opens in new tab');

        return (
            <AnchorElement
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={style}
                className={className}
                aria-label={ariaLabel}
                {...rest}
            >
                {/* Wrap children in the span with z-index */}
                <ButtonTextSpan>{children}</ButtonTextSpan>
            </AnchorElement>
        );
    }

    // Regular button
    return (
        <ButtonElement
            onClick={onClick}
            disabled={disabled}
            type={type}
            style={style}
            className={className}
            {...rest}
        >
            {/* Wrap children in the span with z-index */}
            <ButtonTextSpan>{children}</ButtonTextSpan>
        </ButtonElement>
    );
};

export default StyledButton;